/**
 * Product Domain Bridge Layout
 * 10-step flow: Product Context → ... → Bridge Card → Control.

 * Uses URL ?step= for persistence. Renders ProductDomainLeftSidebar + step content.
 */
import React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  ProductDomainStep,
  PRODUCT_DOMAIN_STEPS,
} from '../../types/bridgeSession';
import { ProductDomainLeftSidebar } from './ProductDomainLeftSidebar';
import { ProductContextScreen } from '../ProductDomain/ProductContextScreen';
import { ProductMechanicsScreen } from '../ProductDomain/ProductMechanicsScreen';
import { CurrentPerformanceScreen } from '../ProductDomain/CurrentPerformanceScreen';
import { BaselineTrajectoryScreen } from '../ProductDomain/BaselineTrajectoryScreen';
import { FutureTargetScreen } from '../ProductDomain/FutureTargetScreen';
import { ProductEntropyScreen } from '../ProductDomain/ProductEntropyScreen';
import { ProductHeatMapScreen } from '../ProductDomain/ProductHeatMapScreen';
import { BridgeFocusScreen } from '../ProductDomain/BridgeFocusScreen';
import { ProductFrictionPrioritiesScreen } from '../ProductDomain/ProductFrictionPrioritiesScreen';
import { ProductDefineConditionsScreen } from '../ProductDomain/ProductDefineConditionsScreen';
import { ProductDomainBridgeCardScreen } from '../ProductDomain/ProductDomainBridgeCardScreen';
import styles from './BridgeSessionLayout.module.css';

function isValidProductStep(s: string | null): s is ProductDomainStep {
  return s != null && PRODUCT_DOMAIN_STEPS.includes(s as ProductDomainStep);
}

export const ProductDomainBridgeLayout: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const stepParam = searchParams.get('step');
  const currentStep: ProductDomainStep = isValidProductStep(stepParam)
    ? stepParam
    : 'product_context';

  const navigateToStep = (step: ProductDomainStep) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('step', step);
      return next;
    });
  };

  const goNext = () => {
    const idx = PRODUCT_DOMAIN_STEPS.indexOf(currentStep);
    if (idx < PRODUCT_DOMAIN_STEPS.length - 1) {
      navigateToStep(PRODUCT_DOMAIN_STEPS[idx + 1]);
    }
  };

  if (!sessionId) {
    return <div>No session</div>;
  }

  const renderContent = () => {
    switch (currentStep) {
      case 'product_context':
        return <ProductContextScreen sessionId={sessionId} onNext={goNext} />;
      case 'product_mechanics':
        return <ProductMechanicsScreen sessionId={sessionId} onNext={goNext} />;
      case 'current_performance':
        return <CurrentPerformanceScreen sessionId={sessionId} onNext={goNext} />;
      case 'baseline_trajectory':
        return <BaselineTrajectoryScreen sessionId={sessionId} onNext={goNext} />;
      case 'future_target':
        return <FutureTargetScreen sessionId={sessionId} onNext={goNext} />;
      case 'product_entropy':
        return <ProductEntropyScreen sessionId={sessionId} onNext={goNext} />;
      case 'product_heat_map':
        return <ProductHeatMapScreen sessionId={sessionId} onNext={goNext} />;
      case 'bridge_focus':
        return <BridgeFocusScreen sessionId={sessionId} onNext={goNext} />;
      case 'friction_priorities':
        return (
          <ProductFrictionPrioritiesScreen
            sessionId={sessionId}
            onNext={goNext}
            onBack={() => navigateToStep('bridge_focus')}
          />
        );
      case 'define_transition':
        return (
          <ProductDefineConditionsScreen
            sessionId={sessionId}
            onNext={goNext}
            onBack={() => navigateToStep('friction_priorities')}
          />
        );
      case 'bridge_card':
        return <ProductDomainBridgeCardScreen sessionId={sessionId} onNext={goNext} />;
      case 'control_activation': {
        const workshopSessionId = searchParams.get('sessionId') || sessionId;
        window.location.href = workshopSessionId ? `/dashboard?sessionId=${workshopSessionId}` : '/dashboard';
        return null;
      }
      default:
        return <ProductContextScreen sessionId={sessionId} onNext={goNext} />;
    }
  };

  return (
    <div className={styles.bridgeSessionLayout}>
      <aside className={styles.sidebar}>
        <ProductDomainLeftSidebar
          currentStep={currentStep}
          onNavigate={navigateToStep}
        />
      </aside>
      <main className={styles.mainContent}>
        <div className={styles.contentWrapper}>{renderContent()}</div>
      </main>
    </div>
  );
};
